import EventEmitter from 'events';
import debug from './debug';
import { AGENT_PORT, IS_DEV } from './env';

let csrfHeaderName = null;
let csrfToken = null;

export function configureCSRF(header, token) {
    csrfHeaderName = header;
    csrfToken = token;
}

export const apiEventEmitter = new EventEmitter();

export const repeatable = (apiCallFunc) => {
    const repeatableApiCall = (body, retryCount = 0) => apiCallFunc(body).catch((res) => {
        if (res && res.status >= 500) {
            retryCount += 1;

            if (retryCount > 6) {
                throw res;
            }

            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve();
                }, Math.pow(2, retryCount) * 5 * 1000);
            }).then(() => {
                return repeatableApiCall(body, retryCount);
            });
        }

        throw res;
    });

    return repeatableApiCall;
};

export const apiCall = (url, ops) => (data) => new Promise((resolve, reject) => {
    const options = { isUserAction: true, ...ops };
    apiEventEmitter.emit('requestStart', { url, ...options });

    const xhr = new window.XMLHttpRequest();
    xhr.onerror = (e) => {
        debug.warn('API post error', url, e);
        reject(e);
    };
    xhr.onload = () => {
        if (xhr.status === 403) {
            debug.log('API', url, xhr.status, xhr.response);
            window.location.reload();
            return;
        }

        let response;
        try {
            response = xhr.response ? JSON.parse(xhr.response) : null;
        } catch (e) {
            debug.warn('API', url, xhr.status, xhr.response);
            e.status = xhr.status;
            reject(e);
            return;
        }

        debug.log('API', url, xhr.status, response);

        const status = (response && typeof response.status === 'number')
            ? (response.status || xhr.status)
            : xhr.status;

        if (status !== 200) {
            if (status >= 500) {
                apiEventEmitter.emit('requestError', { url, ...options });
            }

            if (!response) {
                response = new Error('no response');
            }

            response.status = status;

            reject(response);
        } else {
            resolve(response);
        }
    };
    xhr.open(options?.method || 'POST', url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
    if (IS_DEV) {
        xhr.setRequestHeader('X-Proofpoint-Tracetoken', '123');
    }
    if (csrfHeaderName && csrfToken) {
        xhr.setRequestHeader(csrfHeaderName, csrfToken);
    } else if (!IS_DEV) {
        debug.warn('csrf missing');
    }
    xhr.send(data && JSON.stringify(data));
});

export const fileApiCall = (url) => (data) => {
    return new Promise(async (resolve, reject) => {
        try {
            const headers = {
                Accept: 'application/json',
                'Content-Type': 'application/json;charset=UTF-8',
            };
            if (csrfHeaderName && csrfToken) {
                headers[csrfHeaderName] = csrfToken;
            } else {
                debug.warn('csrf missing');
            }
            const res = await window.fetch(url, {
                method: 'POST',
                headers,
                body: data && JSON.stringify(data),
            });
            if (!res.ok) {
                debug.log('API', url, res.status, res);
                throw new Error(`Network response was not ok. ${res.status}`);
            }
            const blob = await res.blob();
            resolve(blob);
        } catch (e) {
            reject(e);
        }
    });
};

export const AGENT_BASE = `${window.location.protocol}//${window.location.hostname}${IS_DEV ? `:${AGENT_PORT}` : ''}`;

debug.exposeGlobal({ apiCall });
